// A sound's beats, found in this browser (spec find-beats.md). The sound, mono at about 11 kHz, is
// split into four bands, as Scheirer's tempo tracker splits it (1998): each band's loudness rises at
// its onsets, and their rises summed are the onset strength, a frame every 10 ms. The tempo is the
// period that strength repeats at most, from 60 to 180 beats a minute, leaning to 120. The beats
// are the path through the strength that best keeps that period (Ellis's dynamic programming, 2007),
// without the weak beats at either end; each then moves to the sharpest rise of the sound's changes
// within 15 ms, and an end beat off the period is dropped. Pure: no DOM, no Web Audio.

/** The rate the finder reads a sound at: its file's rate divided down to about this. */
export const BEAT_RATE = 11_025;
/** A sound shorter than this has no beat to find. */
export const SHORTEST_MS = 2000;
/** The onset strength's frames. */
const FRAME_MS = 10;
/** The tempi looked for, and the one leant to: one an octave from it weighs a little less. */
const MIN_BPM = 60;
const MAX_BPM = 180;
const LEAN_BPM = 120;
/** How hard the path keeps its period: Ellis's tightness. */
const TIGHTNESS = 100;
/** The quietest a band's loudness counts, below its loudest. */
const FLOOR_DB = 50;
/** How far a beat moves to the sharpest rise near it (a frame and a half), and the rise it needs (3 dB). */
const REFINE_MS = 15;
const RISE = Math.log(2);
/** The window a rise is measured over, before and after a moment. */
const RISE_MS = 10;
/** How far off the period an end beat may lie from its neighbour: past it, it is the sound's start or end. */
const END_SLACK = 0.05;

/** Mono samples at about `BEAT_RATE`, made from a decoded sound's chunks as they come. */
export class MonoSamples {
  readonly rate: number;
  private readonly factor: number;
  private readonly samples: Float32Array;
  private filled = 0;

  /** For a sound of `sampleRate` lasting at most `seconds`. */
  constructor(sampleRate: number, seconds: number) {
    this.factor = Math.max(1, Math.floor(sampleRate / BEAT_RATE));
    this.rate = sampleRate / this.factor;
    this.samples = new Float32Array(Math.max(0, Math.ceil(seconds * this.rate)));
  }

  /** A chunk's channels, from `atS` of the sound: averaged, each `factor` samples to one. */
  add(channels: readonly Float32Array[], atS: number): void {
    const [first] = channels;
    if (first === undefined) return;
    const start = Math.round(atS * this.rate);
    const count = Math.floor(first.length / this.factor);
    const share = 1 / (channels.length * this.factor);
    for (let at = Math.max(0, -start); at < count && start + at < this.samples.length; at += 1) {
      let sum = 0;
      for (const channel of channels) {
        for (let step = 0; step < this.factor; step += 1) sum += channel[at * this.factor + step] ?? 0;
      }
      this.samples[start + at] = sum * share;
    }
    const end = Math.min(this.samples.length, start + count);
    if (end > Math.max(0, start)) this.filled = Math.max(this.filled, end);
  }

  /** The samples so far, up to the latest one added. */
  read(): Float32Array {
    return this.samples.subarray(0, this.filled);
  }
}

interface Biquad {
  b0: number;
  b1: number;
  b2: number;
  a1: number;
  a2: number;
}

/** A second-order filter of the audio EQ cookbook (Bristow-Johnson), normalized. */
function biquad(kind: "low" | "band" | "high", hz: number, rate: number, q: number): Biquad {
  const w0 = (2 * Math.PI * hz) / rate;
  const cos = Math.cos(w0);
  const alpha = Math.sin(w0) / (2 * q);
  const a0 = 1 + alpha;
  const [b0, b1, b2] =
    kind === "low"
      ? [(1 - cos) / 2, 1 - cos, (1 - cos) / 2]
      : kind === "high"
        ? [(1 + cos) / 2, -(1 + cos), (1 + cos) / 2]
        : [alpha, 0, -alpha];
  return { b0: b0 / a0, b1: b1 / a0, b2: b2 / a0, a1: (-2 * cos) / a0, a2: (1 - alpha) / a0 };
}

/** The bands, low to high: a kick's, a snare's body, its crack, and the cymbals' where the rate holds them. */
function bands(rate: number): Biquad[] {
  return [
    biquad("low", 200, rate, Math.SQRT1_2),
    biquad("band", 400, rate, 1),
    biquad("band", 1600, rate, 1),
    ...(rate / 2 > 3500 ? [biquad("high", 3000, rate, Math.SQRT1_2)] : []),
  ];
}

/** Where each frame starts, in samples, one every `FRAME_MS`, and where the last one ends. */
function frameStarts(length: number, rate: number): number[] {
  const step = (rate * FRAME_MS) / 1000;
  const starts: number[] = [];
  for (let frame = 0; Math.round(frame * step) < length; frame += 1) starts.push(Math.round(frame * step));
  starts.push(length);
  return starts;
}

/** A band's loudness each frame in dB, over the frame and the two before it, held above its floor. */
function bandLoudness(samples: Float32Array, starts: readonly number[], filter: Biquad): Float64Array {
  const frames = starts.length - 1;
  const energy = new Float64Array(frames);
  let x1 = 0;
  let x2 = 0;
  let y1 = 0;
  let y2 = 0;
  for (let frame = 0; frame < frames; frame += 1) {
    const from = starts[frame] ?? 0;
    const to = starts[frame + 1] ?? from;
    let sum = 0;
    for (let at = from; at < to; at += 1) {
      const x = samples[at] ?? 0;
      const y = filter.b0 * x + filter.b1 * x1 + filter.b2 * x2 - filter.a1 * y1 - filter.a2 * y2;
      x2 = x1;
      x1 = x;
      y2 = y1;
      y1 = y;
      sum += y * y;
    }
    energy[frame] = sum / Math.max(1, to - from);
  }
  const loud = new Float64Array(frames);
  let top = -Infinity;
  for (let frame = 0; frame < frames; frame += 1) {
    // Before the sound's first frame, its first frame's: a file's start is no onset.
    const spread =
      ((energy[frame] ?? 0) + (energy[Math.max(0, frame - 1)] ?? 0) + (energy[Math.max(0, frame - 2)] ?? 0)) / 3;
    loud[frame] = 10 * Math.log10(spread + 1e-12);
    top = Math.max(top, loud[frame] ?? top);
  }
  for (let frame = 0; frame < frames; frame += 1) loud[frame] = Math.max(loud[frame] ?? 0, top - FLOOR_DB);
  return loud;
}

/** The onset strength each frame: every band's rise of loudness, summed. */
function onsetStrength(samples: Float32Array, rate: number): Float64Array {
  const starts = frameStarts(samples.length, rate);
  const strength = new Float64Array(starts.length - 1);
  for (const filter of bands(rate)) {
    const loud = bandLoudness(samples, starts, filter);
    for (let frame = 1; frame < loud.length; frame += 1) {
      strength[frame] = (strength[frame] ?? 0) + Math.max(0, (loud[frame] ?? 0) - (loud[frame - 1] ?? 0));
    }
  }
  return strength;
}

/** The values smoothed by a Gaussian of `sigma` frames. */
function smoothed(values: Float64Array, sigma: number): Float64Array {
  const reach = Math.max(1, Math.ceil(3 * sigma));
  const weights = Array.from({ length: 2 * reach + 1 }, (_, at) => Math.exp(-0.5 * ((at - reach) / sigma) ** 2));
  const out = new Float64Array(values.length);
  for (let frame = 0; frame < values.length; frame += 1) {
    let sum = 0;
    for (let at = -reach; at <= reach; at += 1) sum += (values[frame + at] ?? 0) * (weights[at + reach] ?? 0);
    out[frame] = sum;
  }
  return out;
}

/** The beat's period in frames: the lag the strength repeats at most, leaning to `LEAN_BPM`. */
function period(strength: Float64Array): number {
  const soft = smoothed(strength, 1);
  const shortest = Math.floor(60_000 / MAX_BPM / FRAME_MS);
  const longest = Math.ceil(60_000 / MIN_BPM / FRAME_MS);
  let zero = 0;
  for (const value of soft) zero += value * value;
  zero /= soft.length;
  // Each lag's score: its autocorrelation, compressed, and the lean (librosa's tempo prior, by its paper).
  const score = (lag: number): number => {
    let sum = 0;
    for (let frame = 0; frame + lag < soft.length; frame += 1) sum += (soft[frame] ?? 0) * (soft[frame + lag] ?? 0);
    const bpm = 60_000 / (lag * FRAME_MS);
    return Math.log1p((1e6 * sum) / Math.max(1, soft.length - lag) / zero) - 0.5 * Math.log2(bpm / LEAN_BPM) ** 2;
  };
  const scores = Array.from({ length: longest - shortest + 3 }, (_, at) => score(shortest - 1 + at));
  const scoreOf = (lag: number) => scores[lag - shortest + 1] ?? -Infinity;
  let best = shortest;
  for (let lag = shortest; lag <= longest; lag += 1) if (scoreOf(lag) > scoreOf(best)) best = lag;
  // Between whole frames: the parabola through the best and its two neighbours.
  const [before, at, after] = [scoreOf(best - 1), scoreOf(best), scoreOf(best + 1)];
  const bend = before - 2 * at + after;
  return bend < 0 ? best + Math.min(0.5, Math.max(-0.5, (0.5 * (before - after)) / bend)) : best;
}

/** The frames of the path through `local` that best keeps `beat` frames apart (Ellis, 2007). */
function beatPath(local: Float64Array, beat: number): number[] {
  const nearest = Math.max(1, Math.round(beat / 2));
  const farthest = Math.round(2 * beat);
  const cost = Array.from({ length: farthest + 1 }, (_, gap) => (gap === 0 ? 0 : TIGHTNESS * Math.log(gap / beat) ** 2));
  const total = new Float64Array(local.length);
  const back = new Int32Array(local.length).fill(-1);
  let top = 0;
  for (const value of local) top = Math.max(top, value);
  let started = false;
  for (let frame = 0; frame < local.length; frame += 1) {
    let best = -Infinity;
    let from = -1;
    for (let gap = nearest; gap <= farthest; gap += 1) {
      const value = (frame - gap >= 0 ? (total[frame - gap] ?? 0) : 0) - (cost[gap] ?? 0);
      if (value > best) {
        best = value;
        from = frame - gap;
      }
    }
    total[frame] = (local[frame] ?? 0) + best;
    // The path starts at the first onset of any strength.
    if (!started && (local[frame] ?? 0) < 0.01 * top) continue;
    started = true;
    back[frame] = from;
  }
  // The last beat: the last peak of the running total above half the peaks' median.
  const peaks: number[] = [];
  for (let frame = 1; frame < total.length - 1; frame += 1) {
    const here = total[frame] ?? 0;
    if (here > (total[frame - 1] ?? 0) && here >= (total[frame + 1] ?? 0)) peaks.push(frame);
  }
  const values = peaks.map((frame) => total[frame] ?? 0).sort((one, other) => one - other);
  const median = values[Math.floor(values.length / 2)];
  if (median === undefined) return [];
  const last = peaks.findLast((frame) => (total[frame] ?? 0) > median / 2) ?? peaks.at(-1) ?? 0;
  const path = [last];
  for (let frame = back[last] ?? -1; frame >= 0; frame = back[frame] ?? -1) path.push(frame);
  return path.reverse();
}

/** The path without its weak beats at either end: those under half the beats' strength, smoothed. */
function trimmed(path: readonly number[], local: Float64Array): number[] {
  const at = path.map((frame) => local[frame] ?? 0);
  const soft = at.map((value, index) => 0.5 * (at[index - 1] ?? 0) + value + 0.5 * (at[index + 1] ?? 0));
  const threshold = 0.5 * Math.sqrt(soft.reduce((sum, value) => sum + value * value, 0) / Math.max(1, soft.length));
  const first = soft.findIndex((value) => value > threshold);
  const last = soft.findLastIndex((value) => value > threshold);
  return first === -1 ? [] : path.slice(first, last + 1);
}

/**
 * The energy of the sound's changes (each sample less the one before: its transients, not a bass
 * note's slow cycles), summed each millisecond, as a running total from the start.
 */
function runningEnergy(samples: Float32Array, rate: number): Float64Array {
  const perMs = rate / 1000;
  const millis = Math.ceil(samples.length / perMs);
  const sums = new Float64Array(millis + 1);
  for (let ms = 0; ms < millis; ms += 1) {
    let sum = 0;
    const end = Math.min(samples.length, Math.round((ms + 1) * perMs));
    for (let at = Math.max(1, Math.round(ms * perMs)); at < end; at += 1) {
      sum += ((samples[at] ?? 0) - (samples[at - 1] ?? 0)) ** 2;
    }
    sums[ms + 1] = (sums[ms] ?? 0) + sum;
  }
  return sums;
}

/** A beat moved to the sharpest rise of the sound's changes within `REFINE_MS`, if one rises 3 dB. */
function sharpest(running: Float64Array, atMs: number, quiet: number): number {
  const last = running.length - 1;
  const energy = (from: number, to: number) =>
    (running[Math.min(last, Math.max(0, to))] ?? 0) - (running[Math.min(last, Math.max(0, from))] ?? 0);
  let best = atMs;
  let rise = RISE;
  for (let ms = Math.max(RISE_MS, atMs - REFINE_MS); ms <= Math.min(last - RISE_MS, atMs + REFINE_MS); ms += 1) {
    const step = Math.log((energy(ms, ms + RISE_MS) + quiet) / (energy(ms - RISE_MS, ms) + quiet));
    if (step > rise) {
      rise = step;
      best = ms;
    }
  }
  return best;
}

/** The beats without an end beat off the period from its neighbour by more than `END_SLACK`. */
function squared(beats: readonly number[], periodMs: number): number[] {
  const off = (one: number, other: number) => Math.abs(Math.abs(other - one) - periodMs) > END_SLACK * periodMs;
  let first = 0;
  let last = beats.length - 1;
  while (last - first >= 2 && off(beats[first] ?? 0, beats[first + 1] ?? 0)) first += 1;
  while (last - first >= 2 && off(beats[last - 1] ?? 0, beats[last] ?? 0)) last -= 1;
  return beats.slice(first, last + 1);
}

/**
 * The beats of mono `samples` at `rate`, in whole milliseconds from their start, rising: none for
 * silence, or for a sound shorter than `SHORTEST_MS`.
 */
export function findBeats(samples: Float32Array, rate: number): number[] {
  if ((samples.length / rate) * 1000 < SHORTEST_MS) return [];
  const strength = onsetStrength(samples, rate);
  let mean = 0;
  for (const value of strength) mean += value;
  mean /= strength.length;
  let spread = 0;
  for (const value of strength) spread += (value - mean) ** 2;
  spread = Math.sqrt(spread / strength.length);
  if (!(spread > 1e-9)) return [];
  for (let frame = 0; frame < strength.length; frame += 1) strength[frame] = (strength[frame] ?? 0) / spread;
  const beat = period(strength);
  const local = smoothed(strength, beat / 32);
  const path = trimmed(beatPath(local, beat), local);
  const running = runningEnergy(samples, rate);
  // A floor for the rise's ratio, far under the sound's own level, so silence before a beat counts.
  const quiet = 1e-6 * ((running[running.length - 1] ?? 0) / Math.max(1, running.length - 1)) * RISE_MS + 1e-12;
  const beats = squared(
    path.map((frame) => sharpest(running, frame * FRAME_MS, quiet)),
    beat * FRAME_MS,
  );
  return [...new Set(beats)].sort((one, other) => one - other);
}
